/**
 * API модуль для работы с авторизацией
 * 
 * Предоставляет функции для входа, выхода и проверки токена авторизации.
 */

import { apiClient } from './client';
import { User } from '../hooks/useAuth';

/**
 * Интерфейс ответа при успешной авторизации
 */
export interface LoginResponse {
  /** JWT токен для авторизации */
  token: string;
  /** Данные авторизованного пользователя */
  user: User;
}

/**
 * Интерфейс данных для входа в систему
 */
export interface LoginRequest {
  /** Номер телефона пользователя */
  phone: string;
  /** Пароль пользователя */
  password: string;
}

/**
 * Авторизация пользователя
 * 
 * Отправляет запрос на сервер для авторизации пользователя по телефону и паролю.
 * В случае успеха возвращает токен и данные пользователя.
 * 
 * @param phone - Номер телефона пользователя
 * @param password - Пароль пользователя
 * @returns Promise с токеном и данными пользователя
 * @throws Error если авторизация не удалась (неверные данные, пользователь не найден и т.д.)
 * 
 * @example
 * ```typescript
 * try {
 *   const { token, user } = await login('+79001234567', 'password123');
 *   console.log('Авторизован:', user.fullName);
 * } catch (error) {
 *   console.error('Ошибка авторизации:', error);
 * }
 * ```
 */
export async function login(phone: string, password: string): Promise<LoginResponse> {
  const response = await apiClient.post<{ status: string; data: LoginResponse }>('/customer/auth/login', {
    phone,
    password,
  });
  
  return response.data.data;
}

/**
 * Проверка валидности токена
 * 
 * Отправляет запрос на сервер для проверки валидности токена авторизации.
 * В случае успеха возвращает данные пользователя.
 * 
 * @param token - JWT токен для проверки
 * @returns Promise с данными пользователя
 * @throws Error если токен невалиден или истек
 * 
 * @example
 * ```typescript
 * try {
 *   const user = await checkToken('eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...');
 *   console.log('Токен валиден, пользователь:', user.fullName);
 * } catch (error) {
 *   console.error('Токен невалиден:', error);
 * }
 * ```
 */
export async function checkToken(token: string): Promise<User> {
  const response = await apiClient.get<{ status: string; data: User }>('/customer/account', {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  
  return response.data.data;
}

/**
 * Выход из системы
 * 
 * Отправляет запрос на сервер для выхода из системы.
 * Сервер может инвалидировать токен или выполнить другие действия.
 * 
 * @returns Promise, который резолвится после успешного выхода
 * 
 * @example
 * ```typescript
 * try {
 *   await logout();
 *   console.log('Успешный выход из системы');
 * } catch (error) {
 *   console.error('Ошибка при выходе:', error);
 * }
 * ```
 */
export async function logout(): Promise<void> {
  await apiClient.post('/auth/logout');
}
